import React from 'react';
import { CSSTransitionGroup } from 'react-css-transition';
import { Draggable, Droppable } from 'react-drag-and-drop';
import { find, uniqBy, last, map, get } from 'lodash';
import ObjectID from 'bson-objectid';
import pt from 'prop-types';

import { imageUrl, classNames } from 'utils';

import Modal from 'components/modal';
import Image from 'components/loading/image';
import { FadeIn } from 'components/transitions';
import Icon from 'components/icon';

import Label from './label';
import FileReader from './file-reader';
import FileDropzone from './file-dropzone';
import ImageCropper from './image-cropper';

const { PUBLIC_URL } = process.env;

const droppableTypes = Object.freeze(['image']);

export default class InputImages extends React.PureComponent {
  static propTypes = {
    id: pt.string,
    name: pt.string,
    help: pt.oneOfType([pt.string, pt.object]),
    label: pt.oneOfType([pt.string, pt.object]),
    typeLabel: pt.string,
    placeholder: pt.oneOfType([pt.string, pt.node]),
    tiny: pt.bool,
    wide: pt.bool,
    single: pt.bool,
    minimal: pt.bool,
    fullSize: pt.bool,
    disabled: pt.bool,
    value: pt.any,
    defaultValue: pt.any,
    maxWidth: pt.number,
    maxHeight: pt.number,
    minWidth: pt.number,
    minHeight: pt.number,
    imageWidth: pt.number,
    imageHeight: pt.number,
    maxCount: pt.number,
    readonlyContent: pt.bool,
    readonly: pt.bool,

    onChange: pt.func,
  };

  static contextTypes = {
    openModal: pt.func.isRequired,
    uploadImages: pt.func.isRequired,
  };

  constructor(props) {
    super(props);

    this.state = {
      dragSourceId: '',
      removedFileIds: new Set(),
      sortOrder: [],
      uploading: false,
      uploadedImages: [],
      enlargedImage: null,
      croppedImage: null,
    };

    /** @type {React.RefObject<HTMLInputElement | null>} */
    this.inputRef = React.createRef();
  }

  setValue(uploadedImages = []) {
    const { single, value, defaultValue } = this.props;
    const { sortOrder, removedFileIds } = this.state;

    let newValue = [
      ...(single ? [value || defaultValue] : value || defaultValue || []),
      ...uploadedImages,
    ].reduce((acc, image) => {
      if (image) {
        const file = image.file || image || {};

        if (!removedFileIds.has(file.id)) {
          acc.push({
            ...image,
            file: {
              id: file.id,
              date_uploaded: file.date_uploaded,
              length: file.length,
              md5: file.md5,
              filename: file.filename,
              content_type: file.content_type,
              metadata: file.metadata,
              url: file.url,
              uploaded_url: file.uploaded_url,
              width: file.width,
              height: file.height,
            },
          });
        }
      }

      return acc;
    }, []);

    if (single) {
      return last(newValue);
    }

    newValue = uniqBy(newValue, (image) => image.file.id);

    if (sortOrder.length > 0) {
      const orderMap = sortOrder.reduce(
        (map, id, i) => map.set(id, i),
        new Map(),
      );

      newValue.sort((a, b) => {
        const indexA = orderMap.get(a.file.id);
        const indexB = orderMap.get(b.file.id);

        if (indexA === indexB) return 0;
        if (indexA === undefined || indexA > indexB) return 1;
        return -1;
      });
    }

    return newValue;
  }

  triggerChange(uploadedImages) {
    const event = new Event('change', { bubbles: true });
    this.inputRef.current.dispatchEvent(event);
    this.props.onChange(event, this.setValue(uploadedImages));
  }

  /** @param {FileList} files */
  onUploadImages = (files) => {
    const { uploadImages } = this.context;
    const { maxCount } = this.props;

    if (
      maxCount !== undefined &&
      this.state.uploadedImages.length >= maxCount
    ) {
      return;
    }

    this.setState({ uploading: true });

    uploadImages(files)
      .then((results) => {
        if (!results) {
          this.setState({ uploading: false });
          return;
        }

        const uploadedImages = results.reduce((images, result) => {
          if (result && result.id) {
            images.push({ file: result, id: ObjectID().toHexString() });
          }
          return images;
        }, []);

        this.setState(
          (state) => {
            const remaining =
              maxCount !== undefined
                ? maxCount - state.uploadedImages.length
                : uploadedImages.length;
            state.uploadedImages.push(...uploadedImages.slice(0, remaining));

            return {
              uploadedImages: state.uploadedImages,
              uploading: false,
            };
          },
          () => this.triggerChange(this.state.uploadedImages),
        );
      })
      .catch(() => {
        this.setState({ uploading: false });
      });
  };

  onClickRemove = (event) => {
    event.preventDefault();

    const fileId = event.currentTarget.dataset.fileid;

    this.context.openModal('Confirm', {
      title: 'Remove image',
      message: <p>Are you sure you want to remove this image?</p>,
      action: 'Remove',
      actionType: 'danger',
      onConfirm: () => {
        this.setState(
          ({ removedFileIds }) => {
            removedFileIds.add(fileId);
            return { removedFileIds };
          },
          () => this.triggerChange(),
        );
      },
    });
  };

  onClickEnlarge = (event) => {
    event.preventDefault();
    const { id } = event.currentTarget.dataset;
    const { value, single = false } = this.props;

    const enlargedValue = single
      ? value
      : find(value, (img) => img.file.id === id);

    const enlargedImage = enlargedValue?.file?.url
      ? enlargedValue.file
      : enlargedValue?.url
      ? enlargedValue
      : null;

    if (enlargedImage) {
      this.setState({ enlargedImage });
    }
  };

  onClickCrop = (event) => {
    event.preventDefault();
    const { id } = event.currentTarget.dataset;
    const { value, single = false } = this.props;
    const croppedImage = single
      ? value
      : find(value, (img) => img.file.id === id);
    if (croppedImage && croppedImage.file && croppedImage.file.url) {
      this.setState({ croppedImage });
    }
  };

  onCloseEnlarged = () => {
    this.setState({ enlargedImage: null });
  };

  onCloseCropped = () => {
    this.setState({ croppedImage: null });
  };

  onSubmitCropper = (file, params) => {
    this.setState({ uploading: true });

    this.context.uploadImages([file]).then((results) => {
      const image = {
        ...params,
        file: results[0],
        id: ObjectID().toHexString(),
      };

      if (!get(image, 'file.id')) {
        return this.setState({ uploading: false }, this.onCloseCropped);
      }

      this.setState(
        (state, props) => {
          const { croppedImage, uploadedImages, removedFileIds } = state;
          const croppedFileId = get(croppedImage, 'file.id');

          uploadedImages.push(image);
          removedFileIds.add(croppedFileId);

          return {
            uploadedImages,
            removedFileIds,
            ...(Array.isArray(props.value)
              ? {
                  sortOrder: map(props.value, (value) =>
                    value.file.id === croppedFileId
                      ? image.file.id
                      : value.file.id,
                  ),
                }
              : undefined),
            uploading: false,
          };
        },
        () => {
          this.triggerChange(this.state.uploadedImages);
          this.onCloseCropped();
        },
      );
    });
  };

  onDragStart = (event) => {
    event.stopPropagation();

    if (this.props.single || this.props.disabled) {
      event.preventDefault();
      return;
    }

    event.dataTransfer.effectAllowed = 'move';
    const dragSourceId = event.currentTarget.dataset.fileid;

    this.setState({ dragSourceId });
  };

  onDragEnd = (event) => {
    event.stopPropagation();

    if (this.props.disabled) {
      return;
    }

    this.setState({ dragSourceId: '' });
  };

  onDragEnter = (event) => {
    event.stopPropagation();

    if (this.props.disabled) {
      return;
    }

    const dragTargetId = event.currentTarget.dataset.fileid;

    if (this.state.dragSourceId === dragTargetId) {
      return;
    }

    this.setSortOrder(this.state.dragSourceId, dragTargetId);
  };

  onDragLeave = (event) => {
    event.stopPropagation();
  };

  setSortOrder(sourceId, targetId) {
    const sortOrder = this.props.value.map((image) => image.file.id);

    const sourceIndex = this.props.value.findIndex(
      (image) => image.file.id === sourceId,
    );

    const targetIndex = this.props.value.findIndex(
      (image) => image.file.id === targetId,
    );

    sortOrder.splice(targetIndex, 0, sortOrder.splice(sourceIndex, 1)[0]);

    this.setState({ sortOrder }, () => this.triggerChange());
  }

  getPlaceholder() {
    const { placeholder, single, tiny, disabled } = this.props;

    if (disabled) {
      return <div className="muted">{`No image${!single ? 's' : ''}`}</div>;
    }

    if (placeholder !== undefined) {
      return this.getCustomPlaceholder(placeholder);
    }

    return (
      <>
        <div className="as-link">{`Add image${!single ? 's' : ''}`}</div>
        &emsp;
        {!tiny && <div className="muted">Drag & drop to upload</div>}
      </>
    );
  }

  getCustomPlaceholder(placeholder) {
    if (typeof placeholder !== 'string' || typeof placeholder === 'object') {
      return placeholder;
    }

    switch (placeholder) {
      case 'favicon':
        return this.getFaviconPlaceholder();
      default:
        return null;
    }
  }

  getFaviconPlaceholder() {
    return (
      <img
        className="form-images-favicon-placeholder"
        src={`${PUBLIC_URL}/favicon.png`}
        alt="favicon"
      />
    );
  }

  render() {
    const {
      id,
      value,
      label,
      typeLabel,
      help,
      single = false,
      minimal = false,
      tiny = false,
      wide = false,
      fullSize = true,
      maxWidth,
      maxHeight,
      minWidth,
      minHeight,
      imageWidth,
      imageHeight,
      maxCount,
      readonlyContent,
      readonly,
      ...props
    } = this.props;

    const { uploading, enlargedImage, croppedImage } = this.state;
    const atMaxCount =
      maxCount !== undefined && this.state.uploadedImages.length >= maxCount;

    let values;
    if (single) {
      values = value ? [value] : [];
    } else {
      if (Array.isArray(value)) {
        values = value;
      } else {
        // for multiloading is enabled in the New Field preview
        values = value ? [value] : [];
      }
    }

    const noSize =
      (maxWidth || maxHeight || minWidth || minHeight) !== undefined;

    const images = values.filter(Boolean).map((image) => {
      const file = image.file || image || {};
      return {
        id: file.id,
        width: maxWidth || file.width > 100 ? file.width : 100,
        height:
          file.height > 100
            ? maxWidth
              ? (maxWidth / file.width) * file.height
              : file.height
            : 100,
        url: imageUrl(file, {
          width: imageWidth ? imageWidth * 2 : undefined,
          height: imageHeight ? imageHeight * 2 : undefined,
          padded: true,
        }),
        file,
      };
    });

    const hasImages = images.length > 0;

    return (
      <div
        className={classNames('form-images', {
          single,
          minimal,
          tiny,
          empty: !hasImages,
          [this.props.name]: this.props.name,
        })}
      >
        <Label label={label} help={help} htmlFor={id} />

        <div
          className={classNames('form-field-input', {
            wide: wide,
            uploading: uploading,
            dragging: Boolean(this.state.dragSourceId),
          })}
          style={{
            minWidth: minWidth || 0,
            minHeight: minHeight || (hasImages ? 0 : undefined),
            // Without a cap the frame keeps the width of its column, so an
            // empty field and a filled one are different sizes
            maxWidth,
            maxHeight,
          }}
        >
          <div className="form-images-emptystate">
            <FadeIn active={!hasImages} transitionAppear={false}>
              <FileDropzone
                className="form-images-dropzone"
                acceptClassName="form-images-dropzone-accepted"
                rejectClassName="form-images-dropzone-rejected"
                accept="image/*"
                disabled={props.disabled}
                onDrop={this.onUploadImages}
                multiple={!single}
              >
                <div
                  className={classNames('form-images-upload', {
                    disabled: props.disabled,
                  })}
                >
                  {this.getPlaceholder()}
                </div>
              </FileDropzone>
            </FadeIn>
          </div>

          <ul className="form-images-list">
            <CSSTransitionGroup>
              {images.map((image, index) => (
                <FadeIn key={image.id || image.url} active={true}>
                  <li
                    className={classNames({
                      dragging: this.state.dragSourceId === image.id,
                      default: index === 0,
                      nosize: noSize,
                    })}
                    data-index={index}
                    style={
                      fullSize
                        ? {
                            maxWidth,
                            maxHeight,
                            minWidth: minWidth
                              ? minWidth - 12
                              : 'calc(100% - 11px)',
                            minHeight: minHeight ? minHeight : undefined,
                          }
                        : undefined
                    }
                  >
                    <Droppable
                      types={droppableTypes}
                      data-fileid={image.id}
                      onDragEnter={this.onDragEnter}
                      onDragLeave={this.onDragLeave}
                    >
                      <Draggable
                        type="image"
                        data-fileid={image.id}
                        onDragStart={this.onDragStart}
                        onDragEnd={this.onDragEnd}
                        className={!single ? 'draggable' : ''}
                      >
                        {single && !props.disabled ? (
                          <FileReader
                            id={`${id}-replace-${image.id}`}
                            accept="image/*"
                            onFiles={this.onUploadImages}
                            multiple={false}
                            className="form-images-replace"
                          >
                            <div className="form-images-image">
                              <Image src={image.url} />
                            </div>
                          </FileReader>
                        ) : (
                          <div className="form-images-image">
                            <Image src={image.url} />
                          </div>
                        )}

                        <div className="form-images-controls">
                          {!props.disabled && (
                            <button
                              data-fileid={image.id}
                              aria-label="Remove image"
                              data-prevent-click="true"
                              className="as-link"
                              onClick={this.onClickRemove}
                              type="button"
                            >
                              <Icon fa="trash" />
                            </button>
                          )}

                          {!tiny && (
                            <>
                              <button
                                data-id={image.id}
                                aria-label="Enlarge image"
                                data-prevent-click="true"
                                className="as-link"
                                onClick={this.onClickEnlarge}
                                type="button"
                              >
                                <Icon fa="search" />
                              </button>

                              {!props.disabled && (
                                <button
                                  data-id={image.id}
                                  aria-label="Crop image"
                                  data-prevent-click="true"
                                  className="as-link"
                                  onClick={this.onClickCrop}
                                  type="button"
                                >
                                  <Icon fa="crop-alt" />
                                </button>
                              )}
                            </>
                          )}
                        </div>
                      </Draggable>
                    </Droppable>

                    <div className="form-images-mask" />
                  </li>
                </FadeIn>
              ))}
            </CSSTransitionGroup>
          </ul>

          <FadeIn
            active={!single && hasImages && !atMaxCount && !props.disabled}
          >
            <FileReader
              id={id}
              accept="image/*"
              onFiles={this.onUploadImages}
              multiple={!single}
              disabled={props.disabled}
            >
              <button
                className="button button-sm button-secondary form-images-addlink view-link"
                type="button"
              >
                Add images
              </button>
            </FileReader>
          </FadeIn>

          <input
            {...props}
            ref={this.inputRef}
            type="hidden"
            defaultValue={undefined}
            value=""
          />
        </div>

        {enlargedImage && (
          <Modal
            onClose={this.onCloseEnlarged}
            chromeless={true}
            cancel={false}
          >
            <div className="form-images-enlarged">
              <Image src={enlargedImage.url} />
            </div>
          </Modal>
        )}

        {croppedImage && (
          <ImageCropper
            image={croppedImage}
            onClose={this.onCloseCropped}
            onSubmit={this.onSubmitCropper}
          />
        )}
      </div>
    );
  }
}
